<template>
    <div class="task-detail-container" data-testid="task-detail">
        <div v-if="loading" class="task-loading shadow-off" data-testid="task-detail__loading-spinner">
            <v-skeleton-loader type="table-heading, list-item-three-line, article, article" :loading="loading" width="100%" />
        </div>
        <div v-else-if="error" class="task-error" data-testid="task-detail__error-alert">
            {{ error }}
        </div>
        <div v-else class="task-content" data-testid="task-detail__main-content">
            <!-- Header with breadcrumbs -->
            <TaskHeader 
                :task="task" 
                :breadcrumbs="breadcrumbs"
                :addeditdata="addeditdata"
                :unexplainedDiff="calculateUnexplainedDifference(task)"
                :threshold="task && typeof task.threshold === 'number' ? task.threshold : (Number(task.threshold) || 0)"
                :explainedValue="calculateExplainedDifference(task)"
                @back="navigateBack()"
                @open-upload-drawer="sendReviewDra('task-detail')"
                @click="handleRefresh"
                @change-status="changeChecklistStatus"
                @user-type-change="handleUserTypeChange"
                @refresh="handleRefresh"
                data-testid="task-detail__header"
            />
            
            <!-- Main task info card -->
            <TaskInfoCard 
                v-if="task && Object.keys(task).length > 0 && !loading"
                :task="task" 
                :key="task.updated_at || Math.random()"
                class="mt-4"
                data-testid="task-detail__info-card"
            />
            
            <!-- Task details with tabs -->
            <TaskDetailsTabbed 
                v-if="task && Object.keys(task).length > 0 && !loading"
                :task="task" 
                :userType="userType"
                class="mt-4"
                @task-updated="handleTaskUpdated"
                @task-instant-updated="handleTaskUpdated"
                @refresh="handleRefresh"
                @update:number-of-evidences="handleNumberOfEvidences"
                :fileNamesList="fileNamesList"
                data-testid="task-detail__tabbed"
            />
            <!-- Task upload modal -->

            <TaskUploadModal
                v-model="sendforreviewdrawer"
                :modelValue="sendforreviewdrawer"
                @update:modelValue="sendforreviewdrawer = $event"
                :bulkworkpaper="bulkworkpaper"
                :fileuploaded="fileuploaded"
                :resetFlag="resetFlag"
                :fileremark="fileremark"
                :uploadiwithoutfile="uploadiwithoutfile"
                :viewtaskdata="task"
                :fileNamesList="fileNamesList"
                :id="projectId"
                :filesOnly="false"
                :numberOfEvidences="numberOfEvidences"
                :pageType="pageType"

                @reset="handleTaskUploadModalReset"
                @remark-updated="handleRemarkUpdated"
                :task="task"
                @refresh="handleRefresh"
                @force-refresh="handleRefresh"
                data-testid="task-detail__upload-modal"
            />
        </div>
    </div>
</template>

<script>
import TaskHeader from './components/TaskHeader.vue';
import TaskInfoCard from './components/TaskInfoCard.vue';
import TaskDetailsTabbed from './components/TaskDetailsTabbed.vue';
import TaskUploadModal from '../components/TaskUploadModal.vue';
import { calculateUnexplainedDifference, calculateExplainedDifference } from '../../../utils/utils';
import { HORIZON_VIEWS } from '../../../utils/horizonConstants';

export default {
    name: 'TaskDetail',
    components: {
        TaskHeader,
        TaskInfoCard,
        TaskDetailsTabbed,
        TaskUploadModal
        // TaskActions
    },
    // No longer using props - will get route params directly
    data() {
        // Get projectId and taskId from route params
        const projectId = this.$route.params.projectId;
        const taskId = this.$route.params.taskId;
        const userType = this.$route.query.userType || 'manager';

        // Always get the latest currentTask from Vuex for editing
        const currentTask = this.$store.getters['tasks/getCurrentTask'] || {};

        return {
            projectId,
            taskId,
            task: {},
            loading: true,
            error: null,
            addeditdata: { ...currentTask }, // Always use a fresh copy from Vuex
            addeditdataerror: {},
            sendforreviewdrawer: false, // Control drawer state
            bulkworkpaper: false,
            fileuploaded: [],
            resetFlag: false,
            fileremark: '',
            uploadiwithoutfile: false,
            viewtaskdata: null,
            fileNamesList: [],
            userType,
            numberOfEvidences: 0,
            pageType: '',
            // Add any other data properties needed for the component
        }
    },
    async created() {
        // Check if task data is already available in the Vuex store
        const storeTask = this.$store.getters['tasks/getTaskById'](this.taskId);
        const taskListData = this.$store.getters['tasks/getTaskListData'];
        const taskListLoaded = taskListData &&
            taskListData.metadata?._id === this.projectId &&
            Array.isArray(taskListData.checklist) &&
            taskListData.checklist.some(t => t && t._id === this.taskId);

        if (storeTask && taskListLoaded) {
            // Data is already in the store (e.g. navigated from Tasklist).
            // Use it directly — no API call needed.
            this.$store.commit('tasks/SET_CURRENT_TASK', storeTask);
            this.task = { ...storeTask };
            this.addeditdata = { ...storeTask };
            this.loading = false;
        } else {
            // Task or supporting list data is missing (e.g. direct URL navigation or page refresh).
            // Fetch everything from the API.
            this.loading = true;
            try {
                await this.$store.dispatch('tasks/fetchTask', {
                    checklistId: this.projectId,
                    taskId: this.taskId
                });
            } catch (err) {
                this.error = err?.message || 'Failed to load task details';
                this.loading = false;
            }
        }
    },
    computed: {
        // Get the task error from the Vuex store
        storeError() {
            return this.$store.getters['tasks/getError'];
        },
        
        // Get the current task from the Vuex store
        storeTask() {
            return this.$store.getters['tasks/getCurrentTask'];
        },

        taskListData() {
            return this.$store.getters['tasks/getTaskListData'] || {};
        },

        breadcrumbs() {
            return [
                {
                    text: 'Home',
                    testId: 'task-detail__breadcrumbs-home',
                    click: () => {
                        const routeObj = this.clientId
                            ? { path: '/financialclose/home', query: { cid: this.clientId } }
                            : { path: '/financialclose/home' };
                        this.$router.push(routeObj);
                    },
                },
                {
                    text: 'Tasks',
                    testId: 'task-detail__breadcrumbs-tasks',
                    click: () => this.navigateBack(),
                },
                {
                    text: this.taskListData?.metadata?.description || '',
                    noTransform: true,
                    disabled: true,
                    testId: 'task-detail__breadcrumbs-desc',
                },
            ];
        },

        // Get client ID from store
        clientId() {
            return this.$store.state.client;
        },
    },
    
    watch: {
        // Watch for changes in the store's current task and update local data
        storeTask: {
            handler(newTask) {
                if (newTask && newTask._id === this.taskId) {
                    this.task = { ...newTask };
                    this.loading = false;
                }
            },
            deep: true
        },
        // Watch store error state
        storeError: {
            handler(error) {
                this.error = error;
                if (error) {
                    // If there's an error and we were loading, stop the loading state
                    this.loading = false;
                }
            }
        },
        // Watch for route param changes
        '$route.params.taskId': {
            handler(newTaskId, oldTaskId) {
                if (newTaskId !== oldTaskId) {
                    this.taskId = newTaskId;
                    
                    // Fetch new task data
                    const storeTask = this.$store.getters['tasks/getTaskById'](newTaskId);
                    if (storeTask) {
                        this.$store.commit('tasks/SET_CURRENT_TASK', storeTask);
                        this.task = { ...storeTask };
                        this.loading = false;
                    } else {
                        this.loading = true;
                        this.$store.dispatch('tasks/fetchTask', {
                            checklistId: this.projectId,
                            taskId: newTaskId
                        });
                    }
                }
            }
        },
        '$route.params.projectId': {
            handler(newProjectId, oldProjectId) {
                if (newProjectId !== oldProjectId) {
                    this.projectId = newProjectId;
                }
            }
        }
    },
    methods: {
        calculateUnexplainedDifference,
        calculateExplainedDifference,
        sendReviewDra(source) {
            this.sendforreviewdrawer = true;
            this.pageType = source;
        },
        handleNumberOfEvidences(val) {
            this.numberOfEvidences = val;
        },
        handleTaskUpdated(newTask) {
            this.task = { ...newTask };
            this.$store.commit('tasks/SET_CURRENT_TASK', newTask);
        },
        handleUserTypeChange(newType) {
            // Update userType in data
            this.userType = newType;
            // Update the route query param (preserve other query params)
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    userType: newType
                }
            });
            // Optionally, reload data or trigger any logic as needed
            // If you want to reload the task, you can call handleRefresh or similar here
            // this.handleRefresh();
        },
        handleRemarkUpdated(newRemark) {
            if (this.task) {
                this.task.remark = newRemark;
                this.task.remarkDate = new Date();
                this.updateTask({ remark: newRemark, remarkDate: this.task.remarkDate });
            }
        },
        /**
         * Handles status change (approve/reject) for the current task, matching tasklist.vue logic.
         * @param {{ id: string, status: string }} param0
         */
        async changeChecklistStatus({ id, status, approver_remark }) {
            this.loading = true;
            try {
                await this.$store.dispatch('tasks/changeChecklistStatus', {
                    checklistId: this.projectId,
                    taskId: id,
                    status,
                    approver_remark
                });
                await this.handleRefresh();
            } catch (err) {
                // Error already handled in Vuex action
                console.log(err);
            } finally {
                this.loading = false;
            }
        },
        handleTaskUploadModalReset() {
            this.fileuploaded = [];
            this.fileremark = '';
            this.uploadiwithoutfile = false;
            this.resetFlag = !this.resetFlag;
        },
        async handleRefresh() {
            // Always reload checklist from backend for true refresh
            this.loading = true;
            try {
                const checklistId = this.projectId;
                if (checklistId) {
                    // Force refresh form data (users, locations, etc.)
                    this.$store.dispatch('tasks/fetchFormDataV3', { forceRefresh: true });
                    // Force API fetch by passing extra param if supported by your store action
                    await this.$store.dispatch('tasks/fetchTask', { checklistId, taskId: this.taskId });
                }
                const updatedTask = this.$store.getters['tasks/getTaskById'](this.taskId);
                if (updatedTask) {
                    this.$store.commit('tasks/SET_CURRENT_TASK', updatedTask);
                    this.task = { ...updatedTask };
                    this.addeditdata = { ...updatedTask };
                    // Always use a fresh copy of upload_v2 for fileNamesList to force reactivity
                    this.fileNamesList = Array.isArray(updatedTask.upload_v2) ? [...updatedTask.upload_v2] : [];
                }
            } catch (err) {
                this.error = err.message || err || 'Unknown error!';
            } finally {
                this.loading = false;
            }
        },
        updateTask(updatedData) {
            
            // Use Vuex store action to update task
            this.$store.dispatch('tasks/updateTask', {
                checklistId: this.projectId,
                taskId: this.taskId,
                updates: updatedData
            })
            .then(() => {
                // Success notification
                this.$toast.success('Task updated successfully');
            })
            .catch(err => {
                console.error('Error updating task:', err);
                this.$toast.error('Failed to update task');
            });
            
            // The task in this component will be updated automatically via the watcher
        },
        
        navigateBack() {
            const query = { cid: this.clientId };

            const tasklistView = this.$store.getters['tasks/getTasklistView'](this.projectId);
            if (tasklistView === HORIZON_VIEWS.HORIZON) {
                query.view = HORIZON_VIEWS.HORIZON;
            }

            if (this.$route.query.userType) {
                query.userType = this.$route.query.userType;
            }

            this.$router.replace({
                path: `/financialclose/project/checklist/${this.projectId}`,
                query,
            });
        },
    },
}
</script>

<style scoped>
.task-detail-container {
    padding: 20px;
}

.task-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.mt-4 {
    margin-top: 16px;
}
</style>